/**
 * FeatureFlagsProvider - fetches /api/account/feature-flags/ on mount and on
 * auth state changes, then makes all flag values available via FeatureFlagsContext.
 *
 * Mount inside QueryClientProvider and BrowserRouter (done in main.tsx).
 *
 * Usage inside React components:
 *   const showSummary = useFlag("coaching_visible_summary", true);
 *   const variant = useVariant("coaching_free_preview", "control");
 *
 * See frontend/shared/featureFlags.ts for stateless helpers and the FlagsMap type.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@shared/api/client";
import { queryKeys } from "@shared/api/queryKeys";
import { isEnabled, getVariant } from "@shared/featureFlags";
import type { FeatureFlagsContextValue, FlagsMap } from "@shared/featureFlags";

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: {},
  isLoading: false,
});

// ---------------------------------------------------------------------------
// React hooks
// ---------------------------------------------------------------------------

/**
 * Boolean flag hook. Returns defaultValue when flags are loading or flag is absent.
 */
export function useFlag(flagKey: string, defaultValue = false): boolean {
  const { flags } = useContext(FeatureFlagsContext);
  return isEnabled(flagKey, flags, defaultValue);
}

/**
 * Variant hook. Returns the variant key string or defaultValue.
 */
export function useVariant(flagKey: string, defaultValue: string | null = null): string | null {
  const { flags } = useContext(FeatureFlagsContext);
  return getVariant(flagKey, flags, defaultValue);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

async function fetchFeatureFlags(): Promise<FlagsMap> {
  try {
    const res = await apiClient.get<FlagsMap>("/api/account/feature-flags/");
    return res.data ?? {};
  } catch {
    // 401 (unauthenticated) or network error - return empty map
    return {};
  }
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Read auth state from cache - no extra API call. Subscribe to changes so
  // flags enable/disable when the user logs in or out.
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!queryClient.getQueryData(queryKeys.auth.user())
  );

  useEffect(() => {
    return queryClient.getQueryCache().subscribe(() => {
      const user = queryClient.getQueryData(queryKeys.auth.user());
      setIsAuthenticated(!!user);
    });
  }, [queryClient]);

  const { data: flags = {}, isLoading } = useQuery({
    queryKey: queryKeys.featureFlags.forUser(),
    queryFn: fetchFeatureFlags,
    staleTime: REFRESH_MS,
    refetchInterval: isAuthenticated ? REFRESH_MS : false,
    retry: false,
    enabled: isAuthenticated,
  });

  // Re-fetch when the auth user query is invalidated (login / logout)
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === "updated" &&
        Array.isArray(event.query.queryKey) &&
        event.query.queryKey[0] === "auth" &&
        event.query.queryKey[1] === "user"
      ) {
        queryClient.invalidateQueries({ queryKey: queryKeys.featureFlags.forUser() });
      }
    });
    return unsubscribe;
  }, [queryClient]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}
